import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faArrowsRotate, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { CSSProperties, ReactNode } from 'react';
import {
  FormattedMessage,
  FormattedNumber,
  MessageDescriptor,
} from 'react-intl';
import styled from 'styled-components';

import {
  InGameCurrency,
  Rarity,
} from '@sorare/core/src/__generated__/globalTypes';
import bestFiveClue from '@sorare/core/src/assets/crafting-clues/best-five.png';
import competitionClue from '@sorare/core/src/assets/crafting-clues/competition.png';
import divisionClue from '@sorare/core/src/assets/crafting-clues/division.png';
import countryClue from '@sorare/core/src/assets/crafting-clues/nationality.png';
import positionClue from '@sorare/core/src/assets/crafting-clues/position.png';
import potionClue from '@sorare/core/src/assets/crafting-clues/potion.png';
import { Amount } from '@sorare/core/src/atoms/Amount';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BrandL,
  BrandS,
  BrandXS,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { ResponsiveImg } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { glossary, wheel } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { qualityNames } from '@sorare/core/src/lib/players';
import { getRewardImageUrl, isXPCurrency } from '@sorare/core/src/lib/rewards';

import { getXpIllustration } from 'lib/wheelReward';

import { Shape } from '../Shape';
import type { Slot_probabilisticBundleSlotConfig } from './__generated__/index.graphql';
import jerseyUrl from './assets/jersey.png';
import ticketUrl from './assets/ticket.png';

const StyledShape = styled(Shape)`
  display: flex;
  justify-content: center;
  align-items: center;
`;
const Content = styled(Vertical).attrs({
  center: true,
})`
  width: 100%;
  padding: var(--double-unit) var(--unit);
`;
const Corner = styled.div`
  position: absolute;
  right: var(--unit);
  top: var(--unit);
`;

const Layout = ({
  percent,
  big,
  illustration,
  description,
}: {
  percent: number;
  big?: boolean;

  illustration?: string | React.ReactNode;
  description?: React.ReactNode;
}) => {
  const Text = big ? BrandL : BrandS;
  return (
    <Content
      style={{
        width: big ? '200px' : undefined,
        height: big ? '317px' : undefined,
      }}
    >
      {big ? null : (
        <Corner>
          <LabelS color="var(--c-nd-500)">
            <FormattedNumber
              value={percent}
              style="percent"
              maximumFractionDigits={2}
            />
          </LabelS>
        </Corner>
      )}
      {typeof illustration === 'string' ? (
        <ResponsiveImg
          src={illustration}
          alt=""
          width={big ? 120 : 60}
          cropWidth={big ? 160 : 80}
        />
      ) : (
        illustration
      )}
      <Text className="text-center">{description}</Text>
    </Content>
  );
};

const PlayerPreview = styled(Horizontal)`
  align-items: flex-end;
  justify-content: center;
  background-size: 52%;
  background-repeat: no-repeat;
  background-position: top;
  aspect-ratio: 1;
`;

const PlayerImageWrapper = styled(ResponsiveImg)`
  border: 1px solid var(--c-nd-300);
  background-color: var(--c-nl-500);
  border-radius: 50%;
  aspect-ratio: 1;
  width: 40%;
  object-fit: contain;
`;
const CardSlotPreview = ({
  players,
  big,
  rarity,
}: {
  players: NonNullable<
    Slot_probabilisticBundleSlotConfig['cards'][number]['players']
  >;
  big?: boolean;
  rarity: Rarity;
}) => {
  return (
    <PlayerPreview
      style={{ backgroundImage: `url(${getRewardImageUrl('card', rarity)})` }}
    >
      {players.map(({ slug, avatarPictureUrl }) => (
        <PlayerImageWrapper
          key={slug}
          src={avatarPictureUrl}
          alt=""
          width={big ? 120 : 60}
          cropWidth={big ? 160 : 80}
        />
      ))}
    </PlayerPreview>
  );
};

type Props = {
  config: Slot_probabilisticBundleSlotConfig;
  big?: boolean;
  style?: CSSProperties;
  rarity: Rarity;
};

export const Slot = ({ config, big, style, rarity }: Props) => {
  const { toMonetaryAmount } = useMonetaryAmount();
  // NOTHING
  let content: ReactNode = (
    <Layout
      percent={config.probability}
      big={big}
      illustration={
        <Vertical
          center
          style={{ aspectRatio: 1, width: big ? '120px' : '60px' }}
        >
          <FontAwesomeIcon icon={faTimes} fontSize={big ? '100px' : '60px'} />
        </Vertical>
      }
      description={<FormattedMessage {...wheel.nothing} />}
    />
  );

  // jersey
  // ticket
  if (config.deliverableItems?.length) {
    const { deliverableItem } = config.deliverableItems[0];
    const src = (() => {
      if (isType(deliverableItem, 'JerseyDeliverableItem')) {
        return deliverableItem.pictureUrl || jerseyUrl;
      }
      if (isType(deliverableItem, 'TicketsDeliverableItem')) {
        return ticketUrl;
      }
      return '';
    })();
    content = (
      <Layout
        percent={config.probability}
        big={big}
        illustration={
          <ResponsiveImg
            src={src}
            alt=""
            width={big ? 120 : 60}
            cropWidth={big ? 160 : 80}
          />
        }
        description={
          isType(deliverableItem, 'JerseyDeliverableItem') &&
          (deliverableItem.name ||
            (deliverableItem.signed ? (
              <FormattedMessage
                id="wheel.slot.deliverableItem.signed"
                defaultMessage="SIGNED JERSEY"
              />
            ) : (
              <FormattedMessage
                id="wheel.slot.deliverableItem"
                defaultMessage="JERSEY"
              />
            )))
        }
      />
    );
  }

  // card
  if (config.cards?.length) {
    const card = config.cards[0];
    const cardPlayers = card.players || [];
    const illustration =
      cardPlayers.length > 0 ? (
        <CardSlotPreview players={cardPlayers} big={big} rarity={card.rarity} />
      ) : (
        getRewardImageUrl('card', card.rarity)
      );

    const description =
      cardPlayers.length === 1
        ? cardPlayers[0]?.abbreviatedName
        : !!card.quality && qualityNames[card.quality];

    content = (
      <Layout
        percent={config.probability}
        big={big}
        illustration={illustration}
        description={description}
      />
    );
  }
  // card shard
  if (config.cardShards?.length) {
    const cardShard = config.cardShards[0];
    content = (
      <Layout
        percent={config.probability}
        big={big}
        illustration={getRewardImageUrl('shard', cardShard.rarity)}
        description={cardShard.quantity}
      />
    );
  }

  // conversion credit
  if (config.conversionCredit) {
    content = (
      <Layout
        percent={config.probability}
        big={big}
        illustration="https://frontend-assets.sorare.com/rewards/credit.png"
        description={
          <Vertical>
            <Amount
              monetaryAmount={toMonetaryAmount(
                config.conversionCredit.maxDiscount
              )}
            />

            <BrandXS color="var(--c-nd-500)">
              <FormattedNumber
                value={config.conversionCredit.percentageDiscount}
                style="percent"
              />
            </BrandXS>
          </Vertical>
        }
      />
    );
  }
  if (config.inGameCurrencies?.length) {
    const igc = config.inGameCurrencies.filter(
      x => x.currency !== InGameCurrency.GOLDEN_WHEEL_TICKET
    )[0];

    const data: Record<
      | 'DIVISION_CRAFT_CLUE'
      | 'POSITION_CRAFT_CLUE'
      | 'COMPETITION_CRAFT_CLUE'
      | 'COUNTRY_CRAFT_CLUE'
      | 'SPECIAL_EVENT_CRAFT_CLUE'
      | 'FIFTY_FIFTY_CRAFT_CLUE',
      { picture: string; name: MessageDescriptor }
    > = {
      [InGameCurrency.DIVISION_CRAFT_CLUE]: {
        picture: divisionClue,
        name: wheel.divisionClue,
      },
      [InGameCurrency.POSITION_CRAFT_CLUE]: {
        picture: positionClue,
        name: wheel.positionClue,
      },
      [InGameCurrency.COMPETITION_CRAFT_CLUE]: {
        picture: competitionClue,
        name: wheel.competitionClue,
      },
      [InGameCurrency.COUNTRY_CRAFT_CLUE]: {
        picture: countryClue,
        name: wheel.countryClue,
      },
      [InGameCurrency.SPECIAL_EVENT_CRAFT_CLUE]: {
        picture: potionClue,
        name: wheel.potionClue,
      },
      [InGameCurrency.FIFTY_FIFTY_CRAFT_CLUE]: {
        picture: bestFiveClue,
        name: wheel.bestFiveClue,
      },
    };
    Object.entries(data).forEach(([clue_name, { picture, name }]) => {
      if (igc?.currency === clue_name) {
        content = (
          <Layout
            percent={config.probability}
            big={big}
            illustration={
              <Vertical
                center
                style={{ aspectRatio: 1, width: big ? '120px' : '60px' }}
              >
                <ResponsiveImg
                  src={picture}
                  alt=""
                  width={big ? 120 : 60}
                  cropWidth={big ? 160 : 80}
                />
              </Vertical>
            }
            description={
              <>
                {igc.amount} <FormattedMessage {...name} />
              </>
            }
          />
        );
      }
    });

    // XP
    if (isXPCurrency(igc?.currency)) {
      const xp = igc.amount;
      content = (
        <Layout
          percent={config.probability}
          big={big}
          illustration={
            <Vertical
              center
              style={{ aspectRatio: 1, width: big ? '120px' : '60px' }}
            >
              <ResponsiveImg
                src={
                  igc.currency === InGameCurrency.COMMON_XP
                    ? // TODO: support common xp low, medium, high assets
                      getRewardImageUrl('xp', Rarity.common)
                    : getXpIllustration(xp)
                }
                alt=""
                width={big ? 120 : 60}
                cropWidth={big ? 160 : 80}
              />
            </Vertical>
          }
          description={
            <>
              {igc.amount} <FormattedMessage {...glossary.xp} />
            </>
          }
        />
      );
    }
    // respin
    if (igc?.currency === InGameCurrency.WHEEL_TICKET) {
      content = (
        <Layout
          percent={config.probability}
          big={big}
          illustration={
            <Vertical
              center
              style={{ aspectRatio: 1, width: big ? '120px' : '60px' }}
            >
              <FontAwesomeIcon
                icon={faArrowsRotate}
                fontSize={big ? '100px' : '60px'}
              />
            </Vertical>
          }
          description={<FormattedMessage {...wheel.spinAgain} />}
        />
      );
    }
  }
  return (
    <StyledShape config={config} style={style} rarity={rarity}>
      {content}
    </StyledShape>
  );
};

Slot.fragments = {
  ProbabilisticBundleSlotConfig: gql`
    fragment Slot_probabilisticBundleSlotConfig on ProbabilisticBundleSlotConfig {
      id
      probability
      ...Shape_probabilisticBundleSlotConfig

      deliverableItems {
        quantity
        deliverableItem {
          slug
          pictureUrl
          name
          ... on JerseyDeliverableItem {
            slug
            signed
          }
        }
      }
      cards {
        quality
        rarity
        players {
          slug
          avatarPictureUrl
          abbreviatedName
        }
      }
      rewardShopItems {
        quantity
        shopItem {
          id
          position
          name
        }
      }
      conversionCredit {
        id
        maxDiscount {
          ...useMonetaryAmount_monetaryAmount
        }
        percentageDiscount
      }
      inGameCurrencies {
        id
        amount
        currency
      }
      cardShards {
        id
        quantity
        rarity
      }
    }
    ${Shape.fragments.ProbabilisticBundleSlotConfig}

    ${useMonetaryAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<Slot_probabilisticBundleSlotConfig>,
};
